import { Component, OnInit, Input } from '@angular/core';
import * as Highcharts from 'highcharts/highstock';
import { MonitorsSlaCompletionMonitorService } from 'e5core-angular-services';
import { MonitorsSlaCompletionMonitorModel } from 'e5core-angular-services';
import { DatePipe } from '@angular/common';
import { MatSelectChange } from '@angular/material';
import { Observable, of } from 'rxjs';

export interface OptionMonth {
  value: DateRange;
  viewValue: string;
}

export interface DateRange {
  startDate: Date;
  endDate: Date;
}

export class MockSlaCompletionService {
  public getMockedSlaCompletionCounts(): Observable<MonitorsSlaCompletionMonitorModel> {
    const incomingData: any = new Array();
    const model: MonitorsSlaCompletionMonitorModel = new MonitorsSlaCompletionMonitorModel();
    incomingData.push({
      completionDate: new Date('2016-12-05T11:52:10.113Z'),
      completedInSla: 10,
      completedPropertyTotal: 38,
      completedTotal: 20,
      inSlaPropertyTotal: 24,
      movingAverage: 50,
      movingAverageWeekly: 50
    }, {
      completionDate: new Date('2016-12-06T11:55:10.113Z'),
      completedInSla: 9,
      completedPropertyTotal: 38,
      completedTotal: 10,
      inSlaPropertyTotal: 24,
      movingAverage: 90,
      movingAverageWeekly: 50
    }, {
      completionDate: new Date('2016-12-07T10:52:10.113Z'),
      completedInSla: 5,
      completedPropertyTotal: 38,
      completedTotal: 8,
      inSlaPropertyTotal: 24,
      movingAverage: 63,
      movingAverageWeekly: 50
    });
    model.data = incomingData;
    return of(model);
  }
}

@Component({
  selector: 'e5core-ui-monitor-sla-completion-highcharts',
  templateUrl: './e5core-ui-monitor-sla-completion-highcharts.component.html',
  styleUrls: ['./e5core-ui-monitor-sla-completion-highcharts.component.css']
})
export class E5coreUiMonitorSlaCompletionHighchartsComponent implements OnInit {

  constructor(private monitorService: MonitorsSlaCompletionMonitorService, private datePipe: DatePipe) {  }

  @Input() slaRangeInDays = 28; // Idea is to display completed work items every day over a period of time. 28 days forms exactly a month
                                // which is a good range to display over.
  @Input() categorisation: number[];

  model: MonitorsSlaCompletionMonitorModel;
  startDate = '2018-01-11T13:21:53.770Z';
  endDate = '2018-30-11T13:35:36.870Z';
  Highcharts = Highcharts;
  chartConstructor = 'chart';
  chartOptions = this.createChartOptions();
  chartCallback = null; // optional function, defaults to null
  updateFlag = false; // optional boolean
  oneToOneFlag = true; // optional boolean, defaults to false
  runOutsideAngularFlag = false;

  // Ready up mock service
  mockCompletionService: MockSlaCompletionService = new MockSlaCompletionService();

  // Let component know if it is dealing with mocked data
  isMocked: boolean;

  // Month drop-down variables
  currentDate: Date = new Date();
  optionMonths: OptionMonth[];
  currentDateRange: DateRange = {
    startDate: new Date(this.currentDate.getFullYear(), this.currentDate.getMonth(), 1),
    endDate: new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() + 1, 0)
  };
  selectedOptionMonth: OptionMonth;
  selectedMonthViewValue: string;

  ngOnInit() {
    this.isMocked = true; // Set to false in production
    this.LoadChartObject(this.startDate, this.endDate);
    this.selectedOptionMonth = {
      value: this.currentDateRange,
      viewValue: this.datePipe.transform(this.currentDateRange.startDate, 'MMMM yyyy')
    };
    this.selectedMonthViewValue = this.selectedOptionMonth.viewValue;
    this.instantiateOptionMonths();
  }

  private addMonths(date: Date, months: number) {
    const newDate = date;
    newDate.setMonth(newDate.getMonth() + months);
    return (newDate);
  }

  private addToOptionMonths(startDate: Date, endDate: Date, negativeDirection: boolean) {
    const dates: Date[] = [];
    let currentDate = new Date(startDate);
    if (!negativeDirection) {
      while (currentDate <= endDate) {
        dates.push(currentDate);
        this.optionMonths.push({
          value: {
            startDate: new Date(currentDate),
            endDate: new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0)
          },
          viewValue: this.datePipe.transform(currentDate, 'MMMM yyyy')
        });
        currentDate = this.addMonths(currentDate, 1);
      }
    }
  }

  private convertToMonthNumber(monthName: string) {
    const months = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December'
    ];
    return months.indexOf(monthName);
  }

  private alterSelectedMonthOption(viewValue: string) {
    const selectedValue = this.optionMonths.filter(f => f.viewValue === viewValue);

    if (selectedValue.length === 0) {
      return;
    }

    this.selectedOptionMonth = {
      value: {
        startDate: selectedValue[0].value.startDate,
        endDate: selectedValue[0].value.endDate
      },
      viewValue: viewValue
    };
  }

  public reloadChart($event: MatSelectChange) {
    this.alterSelectedMonthOption(this.selectedMonthViewValue);
    const selectedDateRange = this.selectedOptionMonth.value;
    this.instantiateOptionMonths();
    this.LoadChartObject(selectedDateRange.startDate, selectedDateRange.endDate);
  }

  private instantiateOptionMonths() {
    this.optionMonths = [];

    const currentlySelectedDate = this.selectedOptionMonth.value.startDate;
    const startDate = new Date(currentlySelectedDate);
    const endDate = new Date(currentlySelectedDate);
    startDate.setMonth(startDate.getMonth() - 5);
    endDate.setMonth(endDate.getMonth() + 5);
    const today = new Date();

    if (endDate > today) {
      endDate.setMonth(today.getMonth());
    }
    this.addToOptionMonths(startDate, endDate, false);
  }

  public LoadChartObject(startDate: any, endDate: any, siteGroup ?: any, category1Id ?: any, category2Id ?: any,
    category3Id ?: any, archive ?: any, totalProperty ?: any) {
      if (this.isMocked) {
        this.mockCompletionService.getMockedSlaCompletionCounts().subscribe(
          model => {
            this.chartOptions = {
              ...this.chartOptions,
              xAxis: this.extractXAxis(model),
              series: this.extractSeries(model)
            };
            this.model = model;
          },
        );
      } else {
        //Commenting out Mock service reference : TODO : to eb replaced with code to call actual service
        // this.monitorService.getMockedSlaCompletionCounts().subscribe(
        //   model => {
        //     this.chartOptions = {
        //       ...this.chartOptions,
        //       xAxis: this.extractXAxis(model),
        //       series: this.extractSeries(model)
        //     };
        //     this.model = model;
        //   },
        // );
      }
  }


  private extractXAxis(model: MonitorsSlaCompletionMonitorModel): any {
    // debugger;
    const output = {
      type: 'datetime',
      ordinal: false,
      mintickInterval: 24 * 3600 * 1000,
      // categories: model.data.map(i => this.datePipe.transform(i.completionDate, 'dd MMM yyyy')),
      title: { rotation: -45 },
      labels: {
       format: '<b class="xAxisLabel">{value: %e %b %y}</b>'
      }
    };
    return (output);
  }

  private extractSeries(model: MonitorsSlaCompletionMonitorModel): any {
    // debugger;
    const series = [];
    // const completedWorkItems = [];
    const completedInSla = [];
    const breachedSla = [];
    const movingAverage = [];
    const completedTotal = [];
    for (let i = 0; i < model.data.length; i++) {
      // completedWorkItems.push(model.data[i].completedTotal);
      
      //TODO :
      // completedInSla.push([model.data[i].completionDate.getTime(), model.data[i].completedInSla]);
      // breachedSla.push([model.data[i].completionDate.getTime(), model.data[i].completedTotal - model.data[i].completedInSla]);
      // movingAverage.push([model.data[i].completionDate.getTime(), model.data[i].movingAverage]);


      // completedTotal.push(model.data[i].completedTotal);
    }
    // series.push({ name: 'Completed Work Items', color: '#00cc99', data: completedWorkItems, drilldown: true });
    // series.push({ name: 'Total Work Completed', color: 'rgba(0, 0, 0, 0)', data: completedTotal, drilldown: true,
    // yAxis: 0, dataLabels: {enabled: true, align: 'center', color: '#000000'} });
    series.push({ name: 'Completed In Sla', color: '#00cc99', data: completedInSla, drilldown: true, yAxis: 0 });
    series.push({ name: 'Expired', color: '#cc3300', data: breachedSla, drilldown: true, yAxis: 0 });
    series.push({ name: 'Moving Average', color: '#FFA500', data: movingAverage, drilldown: true,
    type: 'spline', yAxis: 1, dataLabels: {enabled: false}});

    return (series);
  }

  private drillDown(event) {
    alert(event) ;
  }

  createChartOptions() {
    const _this: E5coreUiMonitorSlaCompletionHighchartsComponent = this;
    return {
      chart: {
        type: 'column',
        events: {
          drilldown: function (event) {
              _this.drillDown(event);
          }
        }
      },
      title: {
        text: 'Work SLA Completion Monitor',
        style: {
          'fontSize': '16px',
          'font-weight': 'bold'
        }
      },
      xAxis: {
        categories: [],
        title: { rotation : -45}
      },
      yAxis: [{
        min: 0,
        title: {
          text: 'Work Item Count',
        },
        labels: {x: -15},
        stackLabels: {
          enabled: true,
          style: {
            fontWeight: 'bold',
            color: 'gray'
          }
        }
      }, {
        min: 0,
        title: {
          text: 'Percentage of Completed Items In Sla',
        },
        opposite: false,
        labels: {x: -15, format: '{value}%'},
        stackLabels: {
          enabled: false,
          style: {
            fontWeight: 'bold',
            color: 'gray'
          }
        }
      }],
      legend: {
        align: 'right',
        x: -30,
        verticalAlign: 'top',
        y: 25,
        floating: true,
        backgroundColor: 'white',
        borderColor: '#CCC',
        borderWidth: 1,
        shadow: false,
        itemStyle: {
          fontSize: '12px',
          font: '12pt',
        }
      },
      tooltip: {
        headerFormat: '<b>{point.x: %e %b %y}</b><br/>',
        pointFormat: '{series.name}: {point.y}<br/>Total: {point.stackTotal}'
      },
      plotOptions: {
      series: {
          stacking: 'normal',
          dataLabels: {
            enabled: true,
            align: 'center',
            color: '#FFFFFF'
          },
        }
      },
      series: [
        {
          name: 'Work Items Completed',
          color: '#00cc99',
          data: []
        }
      ],
      drilldown: {
        series: []
      }
    };
  }

  injectMockInformation(startDate: string, endDate: string) {

  }

  onNavigationCategoryClick(categoryName) {

  }

}



